import {
  Radio,
  FormControlLabel,
  Typography,
  Button,
  Box,
  Paper,
  Collapse,
  CircularProgress
} from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import React from 'react'
import { useRecurly } from '@recurly/react-recurly'
import { useDispatch, useSelector } from 'react-redux'
import { b2cSignupSlice } from '../../../../reducers'
import usePaymentEvent from '../usePaymentEvent'
import { PayPalLogo } from '../../../../components/logos'
import { ERROR_PAYPAL_PAYMENT_FAILED } from '../const'

export const useStyles = makeStyles(() => ({
  submitButton: {
    backgroundColor: '#009CDE',
    '&:hover': { backgroundColor: '#0079A2' },
    borderRadius: '8px'
  },
  submitButtonLabel: {
    display: 'flex',
    color: '#fff',
    alignItems: 'center',
    fontSize: '20px'
  }
}))

export const Paypal = ({ selectedValue }) => {
  const recurly = useRecurly()
  const classes = useStyles()
  const dispatch = useDispatch()
  const plan = useSelector(b2cSignupSlice.selectors.selectPlan)
  const account = useSelector(b2cSignupSlice.selectors.selectAccount)
  const currency = useSelector(b2cSignupSlice.selectors.selectCurrency)
  const coupon = useSelector(b2cSignupSlice.selectors.selectCoupon)
  const checkoutPricing = useSelector(
    b2cSignupSlice.selectors.selectCheckoutPricing
  )
  const paymentInfoSubmitted = useSelector(
    b2cSignupSlice.selectors.selectPaymentInfoSubmitted
  )
  const disabled = useSelector(b2cSignupSlice.selectors.selectDisabled)
  const initiatePayment = usePaymentEvent({
    onComplete: () => {
      dispatch(b2cSignupSlice.actions.setPaymentInfoSubmitted(false))
    }
  })

  const payPal = recurly.PayPal({
    display: {
      displayName: 'Mindtools'
    }
  })

  payPal.on('token', (token) => {
    const payload = {
      planCode: plan?.code,
      currency,
      username: account.username,
      firstName: account.firstName,
      lastName: account.lastName,
      email: account.email,
      tokenId: token.id,
      paymentChannel: 'paypal',
      couponCodes: coupon,
      subscriptionId: account?.subscription?.state !== 'expired' ? account?.subscription?.uuid : null
    }
    dispatch(b2cSignupSlice.actions.setPaymentInfoSubmitted(true))
    initiatePayment.trigger(payload)
  })

  payPal.on('cancel', () => {
    dispatch(b2cSignupSlice.actions.setPaymentInfoSubmitted(false))
  })

  payPal.on('error', () => {
    dispatch(b2cSignupSlice.actions.setPaymentError(ERROR_PAYPAL_PAYMENT_FAILED))
    dispatch(b2cSignupSlice.actions.setPaymentInfoSubmitted(false))
  })

  const handleClick = () => {
    dispatch(b2cSignupSlice.actions.setPaymentInfoSubmitted(true))
    payPal.start({
      description: 'Mindtools Subscription'
    })
  }

  return (
    <Paper
      variant='standard'
      sx={{
        mb: 1,
        p: 2,
        width: '100%',
        borderRadius: '10px',
        border: '1px solid black'
      }}
    >
      <FormControlLabel
        value='paypal'
        control={<Radio />}
        variant='standard'
        disabled={paymentInfoSubmitted || disabled}
        label={
          <Box
            display='flex'
            alignItems='center'
            justifyContent='space-between'
            width='100%'
          >
            <Typography sx={{ flexGrow: 1 }} variant='h6'>
              PayPal
            </Typography>
            <PayPalLogo style={{ position: 'absolute', right: '24px' }} />
          </Box>
        }
        sx={{ width: '100%' }}
      />
      <Collapse in={selectedValue === 'paypal'}>
        <Box mt={2} mb={2} borderBottom='1px solid black' />

        {/* Order total */}
        <Box
          display='flex'
          justifyContent='space-between'
          alignItems='center'
          mb={2}
        >
          <Typography variant='h6' fontWeight='bold'>
            Order Total
          </Typography>
          <Typography variant='h6' fontWeight='bold'>
            {checkoutPricing?.currency?.symbol}
            {checkoutPricing?.now?.total}
          </Typography>
        </Box>

        {/* Pay button */}
        <Button
          variant='contained'
          fullWidth
          className={classes.submitButton}
          onClick={handleClick}
          startIcon={paymentInfoSubmitted && (<CircularProgress size={18} color='inherit' />)}
          disabled={paymentInfoSubmitted}
        >
          <Box className={classes.submitButtonLabel}>
            Continue with&nbsp;
            <PayPalLogo />
          </Box>
        </Button>

        <Typography variant='body2' mt={2}>
          You will be directed to Paypal to finish your payment. Paypal may be
          blocked by your browser&apos;s pop-up blocker for this page, then
          refresh.
        </Typography>
      </Collapse>
    </Paper>
  )
}
